import {createSelector} from 'reselect';

import {ErrorWithData} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {AppParametricSelector} from '../../../../helpers/reselectHelpers';
import {ConversationBasesSelector} from '../../../data/conversations/conversationsSelectors';
import {getFindTeammate} from '../../../data/teammates/lazyTeammatesSelectors';
import {CellsContext} from '../../cells/context/cellsContext';
import {InboxesVisualConversationSelection} from '../../cells/helpers/cellsPanelSelectionHelpers';
import {InboxesParameters} from '../../inboxes/parameters/inboxesParameters';
import {computeIsBulkActionRequired} from '../viewerPanelHelpers';

/*
 * Helpers.
 */

export function makeGetIsBulkActionRequired<P>(
  getInboxesParameters: AppParametricSelector<P, InboxesParameters>,
  getConversationSelection: AppParametricSelector<P, InboxesVisualConversationSelection>,
): AppParametricSelector<P, boolean> {
  return createSelector([getInboxesParameters, getConversationSelection], computeIsBulkActionRequired);
}

/*
 * Star.
 */

export type CellsContextSelector<P> = AppParametricSelector<P, CellsContext>;

export function makeGetStarredTagId<P>(getCellsContext: CellsContextSelector<P>) {
  const getTeammate = makeGetTeammate(getCellsContext);
  return createSelector([getTeammate], (teammate) => teammate.starredTagId);
}

function makeGetTeammate<P>(getCellsContext: CellsContextSelector<P>) {
  return createSelector([getCellsContext, getFindTeammate], (cellsContext, findTeammate) => {
    const teammate = findTeammate(
      cellsContext.teammateId,
      'viewerPanelConversationsSelectors makeGetTeammate',
    );
    if (!teammate) {
      throw new ErrorWithData('A teammate could not be found for the current context.', {
        teammateId: cellsContext.teammateId,
      });
    }

    return teammate;
  });
}

export function makeGetAreAllStarred<P>(
  getCellsContext: CellsContextSelector<P>,
  getSelectedConversations: ConversationBasesSelector<P>,
) {
  const getStarredTagId = makeGetStarredTagId(getCellsContext);
  return createSelector(
    [getSelectedConversations, getStarredTagId],
    (selectedConversations, starredTagId) => {
      if (!starredTagId) {
        return false;
      }

      return selectedConversations.every((c) => c.tagIds.includes(starredTagId));
    },
  );
}
